const { stripNonDigits } = require('../quotes/utils');
const uiDataUtils = require('./ui-data');

/**
 * Create a unique id for a driver based on their attributes.
 *
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} dob Date in the format mm/dd/yyyy.
 * @returns {string} String formattedPhone as '${firstName}-${lastName}-${dob}'
 */
function getDriverId(firstName, lastName, dob) {
  return `${firstName}-${lastName}-${dob}`;
}

/**
 * Ensure a val isn't an empty string, an empty object, empty array, undefined or null.
 *
 * @param {*} val
 * @returns {boolean}
 */
function hasVal(val) {
  const type = typeof val;
  if (Array.isArray(val) || type === 'string') return val.length > 0;
  if (val === null || type === 'undefined') return false;
  if (type === 'object') return Object.keys(val).length > 0;
  return true;
}

/**
 * Formats a phone number with dashes
 *
 * @param {string} phoneNumber
 * @returns {string|null} A formatted phone number, e.g. "610-555-5555"
 */
function formatPhone(phoneNumber) {
  const cleaned = stripNonDigits(phoneNumber);
  const parts = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  return parts && `${parts[1]}-${parts[2]}-${parts[3]}`;
}

/**
 * Formats a phone number with dashes
 *
 * @param {string} phoneString
 * @returns {string|null} A formatted phone number, e.g. "+16105555555"
 */
function formatPhoneUS(phoneString) {
  const cleaned = stripNonDigits(phoneString);
  const isInternational = cleaned.length === 11 && cleaned[0] === '1';
  const isLocal = cleaned.length === 10;
  if (isInternational) return `+${cleaned}`;
  if (isLocal) return `+1${cleaned}`;
  return null;
}

/**
 * Test whether a phone number is valid under the North American Numbering Plan
 * (see https://en.wikipedia.org/wiki/North_American_Numbering_Plan)
 *
 * @param {string} phoneNumber tolerates non-numeric characters and whitespace, 10-11 digits
 * @returns {boolean} phoneNumber is valid
 */
function validatePhone(phoneNumber) {
  const cleaned = stripNonDigits(phoneNumber);

  const basicPattern = /^1?[2-9][0-8][0-9][2-9]\d{6}$/g;
  const no555 = /^1?\d{3}555\d{4}$/g;
  const noX11 = /^1?\d{4}11\d{4}$/g;
  const no800 = /^1?800\d{7}$/g;
  const noRepeating = /^1?(.)\1{9}$/g;

  return basicPattern.test(cleaned) && ![no555, noX11, no800, noRepeating].some(pattern => pattern.test(cleaned));
}

/**
 * Validate string is in valid email form.
 * Must have 3 groups of characters broken up by an "@"" and a ".'
 * Valid examples: b@c.com, b+1@c.com, b.b@c.com, b-b@c.com
 *
 * @param {string} value
 * @returns {boolean}
 */
function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+/.test(value);
}

/**
 * Capitalize the first letter of the string, lowercase the rest.
 *
 * @param {string} str
 * @returns {string}
 */
function capitalize(str) {
  return str?.charAt(0).toUpperCase() + str?.toLowerCase().slice(1) || '';
}

module.exports = {
  ...uiDataUtils,
  getDriverId,
  hasVal,
  formatPhone,
  formatPhoneUS,
  validatePhone,
  isValidEmail,
  capitalize
};
